import React from 'react';
import { Form, Button, Card, Spinner, Alert } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import Header from './Header.js';
import LineChart from './LineChart.js';
import DataTable from './DataTable.js';
import { DataContext } from './DataContextProvider.js';
import './Insights.css';

export default function Insights() {
  const [messages, setMessages] = React.useState([]);
  const [inputMessage, setInputMessage] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const dataContext = React.useContext(DataContext);
  const messagesEndRef = React.useRef(null);
  const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : '';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  React.useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getArticleDataFromContext = () => {
    // Use data already loaded in dataContext instead of fetching
    if (!dataContext.itemData || !dataContext.itemData.results) {
      return null;
    }

    const start = dataContext.start.toISOString().substring(0, 10);
    const end = dataContext.end.toISOString().substring(0, 10);
    const today = new Date().toISOString().substring(0, 10);
    
    return {
      dateRange: { start, end, today },
      blog: dataContext.blog,
      articles: dataContext.itemData.results,
      filters: dataContext.globalFilters
    };
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setIsLoading(true);
    setError(null);

    // Add user message to chat
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);

    try {
      // Get current article data from dataContext
      const articleData = getArticleDataFromContext();
      
      if (!articleData) {
        throw new Error('No article data available. Please wait for data to load.');
      }

      // Send to backend API for OpenAI processing
      console.log("articleData", articleData);
      console.log("tests", userMessage)
      const response = await fetch(`${apiBase}/api/insights/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          articleData: articleData,
          conversationHistory: messages.slice(-10), // Send last 10 messages for context
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get AI response');
      }

      const data = await response.json();
      console.log(data.response)
      // Add AI response to chat
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: data.response 
      }]);
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err.message || 'Failed to get AI response. Please try again.');
      // Remove the user message if there was an error
      setMessages(prev => prev.slice(0, -1));
      setInputMessage(userMessage); // Restore the user's message
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
    setError(null);
  };

  // Clean up markdown formatting to reduce excessive spacing
  const cleanMarkdown = (text) => {
    if (!text) return text;
    
    // Remove extra blank lines between list items
    let cleaned = text.replace(/(\n\s*[-*\d.]+\s+[^\n]+)\n\n+(?=\s*[-*\d.]+\s+)/g, '$1\n');
    
    // Remove extra blank lines before list items after headings
    cleaned = cleaned.replace(/(#+\s+[^\n]+)\n\n+(\s*[-*\d.]+\s+)/g, '$1\n\n$2');
    
    // Normalize spacing around headings (max one blank line)
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
    
    return cleaned;
  };

  const suggestedPrompts = [
    "What are the top performing articles this period?",
    "Identify traffic patterns and trends",
    "What content gaps should we address?",
    "Analyze conversion rates across articles",
    "Compare organic vs paid traffic performance"
  ];

  const handleSuggestedPrompt = (prompt) => {
    setInputMessage(prompt);
  };

  return (
    <>
      <Header title="Content Insights" location="/insights" />
      
      <DataTable item="articles" title="Article" paginate={true} />

      <div className="insights-chat-container">
        <Card className="insights-chat-card">
          <Card.Header className="insights-chat-header">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <span className="ai-icon">🤖</span> AI Content Analyst
              </h5>
              {messages.length > 0 && (
                <Button 
                  variant="outline-secondary" 
                  size="sm" 
                  onClick={clearChat}
                >
                  Clear Chat
                </Button>
              )}
            </div>
            <small className="text-muted d-block">
              Ask questions about your content performance, traffic patterns, and get recommendations
            </small>
            <small className="text-muted d-block mt-1">
              <strong>💡 Tip:</strong> The AI analyzes only the filtered data shown in the table above. Use filters to refine your dataset before asking questions.
            </small>
          </Card.Header>
          
          <Card.Body className="insights-chat-body">
            {messages.length === 0 ? (
              <div className="insights-welcome">
                <h6>Suggested Questions:</h6>
                <div className="suggested-prompts">
                  {suggestedPrompts.map((prompt, idx) => (
                    <Button
                      key={idx}
                      variant="outline-primary"
                      size="sm"
                      className="suggested-prompt-btn"
                      onClick={() => handleSuggestedPrompt(prompt)}
                    >
                      {prompt}
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="messages-container">
                {messages.map((msg, idx) => (
                  <div 
                    key={idx} 
                    className={`message ${msg.role === 'user' ? 'user-message' : 'assistant-message'}`}
                  >
                    <div className="message-content">
                      {msg.role === 'assistant' && <span className="message-icon">🤖</span>}
                      <div className="message-text">
                        {msg.role === 'assistant' ? (
                          <ReactMarkdown
                            components={{
                              p: ({node, ...props}) => <p style={{margin: '0 0 0.2rem 0'}} {...props} />,
                              ol: ({node, ...props}) => <ol style={{margin: '0', padding: '0 0 0 1.25rem'}} {...props} />,
                              ul: ({node, ...props}) => <ul style={{margin: '0', padding: '0 0 0 1.25rem'}} {...props} />,
                              li: ({node, ...props}) => <li style={{margin: '0', padding: '0', lineHeight: '1.3'}} {...props} />,
                              h1: ({node, ...props}) => <h1 style={{fontSize: '1.2rem', fontWeight: 600, margin: '0.2rem 0 0 0'}} {...props} />,
                              h2: ({node, ...props}) => <h2 style={{fontSize: '1.1rem', fontWeight: 600, margin: '0.2rem 0 0 0'}} {...props} />,
                              h3: ({node, ...props}) => <h3 style={{fontSize: '1rem', fontWeight: 600, margin: '0.2rem 0 0 0'}} {...props} />,
                            }}
                          >
                            {cleanMarkdown(msg.content)}
                          </ReactMarkdown>
                        ) : (
                          msg.content
                        )}
                      </div>
                      {msg.role === 'user' && <span className="message-icon">👤</span>}
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="message assistant-message">
                    <div className="message-content">
                      <span className="message-icon">🤖</span>
                      <div className="message-text">
                        <Spinner animation="border" size="sm" /> Analyzing...
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
            
            {error && (
              <Alert variant="danger" className="mt-3" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
          </Card.Body>
          
          <Card.Footer>
            <Form onSubmit={sendMessage}>
              <div className="d-flex gap-2">
                <Form.Control
                  type="text"
                  placeholder="Ask about traffic patterns, content performance, or get recommendations..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  disabled={isLoading}
                />
                <Button 
                  type="submit" 
                  variant="primary"
                  disabled={!inputMessage.trim() || isLoading}
                >
                  {isLoading ? <Spinner animation="border" size="sm" /> : 'Send'}
                </Button>
              </div>
            </Form>
          </Card.Footer>
        </Card>
      </div>
    </>
  );
}
